import type { AxiosInstance } from 'axios';
import { unwrap } from '../core/envelope';

/**
 * Live POST /api/Image/upload contract (re-probed 2026-05-29):
 *
 *   Request : multipart/form-data with field name `file` (lowercase)
 *   Response: {
 *     url:         "<tenantId>/images/<aa>/<sha256>.png"  ← storage path (NOT directly fetchable, 404)
 *     downloadUrl: "/api/Image/<sha256>.png"              ← real route (200, requires Bearer auth)
 *     sha256:      "<64-hex>"
 *     fileName:    "<original-name>"
 *     sizeBytes:   <int>
 *     contentType: "<mime>"
 *   }
 *
 * Use `downloadUrl` everywhere — the bare `url` is server-side metadata
 * only. See G61 in BACKEND_GAPS.md (image route requires Bearer auth,
 * so `<img src=...>` can't fetch it directly — see AuthImg component).
 */
export interface ImageUploadResult {
  /** Server-side storage path. NOT directly fetchable — use `downloadUrl`. */
  url: string;
  /** API route that actually serves the image. Requires Bearer auth. */
  downloadUrl: string;
  sha256?: string;
  fileName: string;
  sizeBytes: number;
  contentType: string;
}

/**
 * Wraps the multipart upload endpoint so consumers don't have to
 * remember the field name or rebuild the FormData each time. Pass
 * any `Blob` / `File` and you get the persisted URL back.
 */
export interface ImageUploadService {
  upload: (file: Blob, fileName?: string) => Promise<ImageUploadResult>;
}

export function createImageUploadService(client: AxiosInstance): ImageUploadService {
  return {
    upload: async (file, fileName) => {
      const fd = new FormData();
      // Field name must be `file` — `File`, `image` etc. are rejected.
      fd.append('file', file, fileName ?? 'upload.bin');
      const resp = await client.post('/Image/upload', fd, {
        // Let the browser set the boundary; do NOT set Content-Type
        // ourselves — Axios will use the right multipart header from
        // FormData automatically.
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return unwrap<ImageUploadResult>(resp.data);
    },
  };
}
